import { Close } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  FormControl,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ChangeEvent, FormEvent, useCallback, useMemo, useState } from "react";
import { useGetProductsQuery } from "src/api/services/productsApi";
import { AlertProps, PatientProfile, Product } from "src/api/types";
import { desktopSx } from "src/utils/styles/modalStyles";
import LoadingButton from "../../Buttons/LoadingButton";
import { useCreateManualChargeMutation } from "src/api/services/invoicesApi";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import PaymentMethodSelect from "src/shared/PaymentMethodSelect";

interface Props {
  open: boolean;
  onClose: () => void;
  patient: PatientProfile;
}

const ChargePatientModal = ({ onClose, open, patient }: Props) => {
  const { products } = useGetProductsQuery(
    {
      gender:
        patient.profile.gender != null ? patient.profile.gender : undefined,
      from: 0,
      to: 100,
    },
    {
      selectFromResult: ({ data }) => {
        return { products: data?.data ?? [] };
      },
    },
  );

  const [chargePatient] = useCreateManualChargeMutation();
  const [amount, setAmount] = useState<number>(0);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [alert, setAlert] = useState<AlertProps>({
    display: false,
    message: "",
    severity: "success",
  });
  const errorLog = useSentryLogger("error");

  const disableSubmit = useMemo(
    () =>
      amount <= 0 ||
      selectedProduct == null ||
      selectedPayment.length <= 0 ||
      description.length <= 0,
    [amount, description.length, selectedPayment.length, selectedProduct],
  );

  const handleAmount = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setAmount(Number(e.target.value));
  }, []);

  const handleDescription = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  }, []);

  const handleProduct = useCallback(
    (_: React.SyntheticEvent<Element, Event>, value: Product | null) => {
      setSelectedProduct(value);
    },
    [],
  );

  const handleClose = useCallback(() => {
    setAlert({
      display: false,
      message: "",
      severity: "success",
    });
    onClose();
  }, [onClose]);

  const handleChargePatient = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (selectedProduct == null) return;
      setLoading(true);

      await chargePatient({
        amount,
        description,
        patient_id: patient.id,
        payment_method_id: selectedPayment,
        product_category_id: selectedProduct.category.id,
        product_id: selectedProduct.id,
      })
        .unwrap()
        .then(() => {
          setSelectedPayment("");
          setSelectedProduct(null);
          setAmount(0);
          setDescription("");
          setLoading(false);
          setAlert({
            display: true,
            message: `Successfully charged patient - $${amount} for ${description}`,
            severity: "success",
          });
        })
        .catch((err) => {
          errorLog("Error manually charging patient", err);
          setLoading(false);
          setAlert({
            display: true,
            message: `Unable to charge the patient. Please try again or use a different payment method.`,
            severity: "error",
          });
        });
    },
    [
      amount,
      chargePatient,
      description,
      errorLog,
      patient.id,
      selectedPayment,
      selectedProduct,
    ],
  );

  return (
    <Modal open={open}>
      <Stack sx={desktopSx} gap={3}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h6" fontWeight={600}>
            Charge patient
          </Typography>
          <IconButton onClick={handleClose} sx={{ alignSelf: "baseline" }}>
            <Close />
          </IconButton>
        </Stack>
        <Stack component={"form"} gap={2} onSubmit={handleChargePatient}>
          <FormControl>
            <TextField
              label="Amount ($)"
              type="number"
              value={amount}
              onChange={handleAmount}
              required
            />
          </FormControl>
          <FormControl>
            <Autocomplete
              options={products}
              getOptionLabel={(p) => p.name}
              value={selectedProduct}
              onChange={handleProduct}
              renderInput={(params) => (
                <TextField {...params} label="Product" required />
              )}
            />
          </FormControl>
          <FormControl>
            <TextField
              label="Description"
              value={description}
              onChange={handleDescription}
              required
            />
          </FormControl>
          <PaymentMethodSelect
            patientId={patient.id}
            selected={selectedPayment}
            setSelected={setSelectedPayment}
          />
          <LoadingButton
            disabled={disableSubmit}
            loading={loading}
            type="submit"
          >
            Submit
          </LoadingButton>
          {alert.display ? (
            <Alert variant="filled" severity={alert.severity}>
              {alert.message}
            </Alert>
          ) : null}
        </Stack>
      </Stack>
    </Modal>
  );
};

export default ChargePatientModal;
